import type { PlayerColor } from '@/shared/board/types'
import type { BonusKind } from '@/shared/game/types'
import { cornerIndexes, QUARTER } from './board'
import { type GameState, ringOccupant, type TokenRef } from './state'

/**
 * Бонусы на поле и щиты — порт App\Domain\Ludo\Bonus\BonusField.
 *
 * Нужен партии за одним устройством: у неё нет сервера, и режим с бонусами
 * там считает браузер. Числа и правила — те же, что в PHP; почему именно
 * такие, объяснено там и в docs/BONUSES.md.
 *
 * Случайность — снаружи: в тестах её задают, в игре это Math.random. На
 * равенство с сервером расстановка бонусов не проверяется — генераторы у них
 * разные; проверяются правила: куда класть нельзя, срок, щит.
 */

/** В каком круге появляется первый набор. */
export const FIRST = 2

/** Шанс, что в начале круга подсыплются новые бонусы — не дожидаясь, пока уйдут старые. */
export const CHANCE = 0.55

/** Сколько кругов подряд может обойтись без подсыпки — дальше она обязательна. */
export const MAX_GAP = 3

/** Сколько бонусов выпадает за раз — вес каждого количества. Чаще по одному. */
export const DROP_WEIGHTS: Record<number, number> = { 1: 5, 2: 3, 3: 1 }

/** На пустое поле — на столько больше, чтобы оно не оживало одиночным значком. */
export const DROP_EMPTY_EXTRA = 1

/** Больше этого на поле не лежит. */
export const MAX_ON_FIELD = 6

/** Срок жизни бонуса в кругах — у каждого свой, чтобы выпавшие вместе не сгорали строем. */
export const LIFETIME_MIN = 2
export const LIFETIME_MAX = 4

/** Срок бонуса, сохранённого до того, как срок стал своим у каждого. */
export const LIFETIME_LEGACY = 3

/** Сколько ходов владельца держится щит, считая тот, в котором он подобран. */
export const SHIELD_TURNS = 5

/** Во сколько раз один вид выпадает чаще другого. */
export const WEIGHT: Record<BonusKind, number> = { chest: 2, roll: 2, shield: 1 }

/** Что бывает в сундуке — ChestPrize. Монет за одним устройством не бывает. */
export type ChestPrize = 'coins' | 'roll' | 'shield'

export const PRIZE_WEIGHT: Record<ChestPrize, number> = { coins: 2, roll: 2, shield: 1 }

/** until — круг, в начале которого бонус сгорает; у сохранённых раньше его нет. */
export type BonusItem = { index: number; kind: BonusKind; round: number; until?: number }

export type Shield = { color: PlayerColor; token: number; left: number }

export type BonusField = {
  readonly first: PlayerColor
  readonly round: number
  readonly lastSpawn: number
  readonly items: readonly BonusItem[]
  /** left — у щитов, сохранённых до того, как щит стал держаться дольше, его нет. */
  readonly shields: readonly (Omit<Shield, 'left'> & { left?: number })[]
}

/** Событие для ленты — в том же виде, что пишет сервер. */
export type BonusEvent = Record<string, unknown>

/** Поле в начале партии: бонусов нет, первый круг ещё не начался. */
export function startField(first: PlayerColor): BonusField {
  return { first, round: 0, lastSpawn: 0, items: [], shields: [] }
}

/**
 * Расстояния от старта четверти, на которых бонус может лежать: не старт,
 * не угол, не вход в дом и не клетка между ним и следующим стартом.
 */
export function offsets(): number[] {
  const excluded = new Set([
    0,
    QUARTER - 2,
    QUARTER - 1,
    ...cornerIndexes().map((i) => i % QUARTER),
  ])

  return Array.from({ length: QUARTER }, (_, index) => index).filter((i) => !excluded.has(i))
}

/** Клетки кольца, куда бонус может лечь: разрешённые расстояния во всех четвертях. */
export function bonusCells(): number[] {
  return [0, 1, 2, 3].flatMap((quarter) => offsets().map((offset) => quarter * QUARTER + offset))
}

/**
 * Начался ход цвета: щитам этого цвета — на ход меньше, а если это первый
 * игрок — начать круг, сжечь отлежавшее свой срок и, может быть, подсыпать
 * новых к оставшимся.
 */
export function beginFieldTurn(
  field: BonusField,
  color: PlayerColor,
  state: GameState,
  random: () => number = Math.random,
): [BonusField, BonusEvent[]] {
  const shields = field.shields
    .map((shield) =>
      shield.color === color ? { ...shield, left: (shield.left ?? 1) - 1 } : shield,
    )
    .filter((shield) => (shield.left ?? 1) > 0)

  if (color !== field.first) return [{ ...field, shields }, []]

  const round = field.round + 1
  const events: BonusEvent[] = []
  const items = field.items.filter((item) => round < untilOf(item))
  const gone = field.items.filter((item) => round >= untilOf(item)).map((item) => item.index)

  if (gone.length > 0) events.push({ action: 'expire', cells: gone })

  let lastSpawn = field.lastSpawn

  if (drops(round, lastSpawn, items, random)) {
    const set = spawn(state, items, random)

    if (set.length > 0) {
      for (const placed of set) {
        const life = LIFETIME_MIN + Math.floor(random() * (LIFETIME_MAX - LIFETIME_MIN + 1))

        items.push({ index: placed.cell, kind: placed.kind, round, until: round + life })
      }

      lastSpawn = round
      events.push({ action: 'spawn', items: set })
    }
  }

  return [{ ...field, round, lastSpawn, items, shields }, events]
}

/** Доживает ли бонус последний круг — экран предупреждает, что он сгорит. */
export function isExpiring(field: BonusField, item: BonusItem): boolean {
  return field.round >= untilOf(item) - 1
}

function untilOf(item: BonusItem): number {
  return item.until ?? item.round + LIFETIME_LEGACY
}

/**
 * Подсыпать ли в этом круге: первый набор — ровно в круге FIRST, пустое поле
 * пополняется всегда, засуха дольше MAX_GAP обрывается.
 */
function drops(
  round: number,
  lastSpawn: number,
  items: readonly BonusItem[],
  random: () => number,
): boolean {
  if (round < FIRST || items.length >= MAX_ON_FIELD) return false

  if (items.length === 0 || round - lastSpawn >= MAX_GAP) return true

  return random() < CHANCE
}

/** Фишка закончила шаг на клетке кольца — забрать то, что там лежит. */
export function takeBonus(field: BonusField, index: number): [BonusField, BonusKind | null] {
  const at = field.items.findIndex((item) => item.index === index)

  if (at === -1) return [field, null]

  const kind = field.items[at].kind

  return [{ ...field, items: field.items.filter((_, i) => i !== at) }, kind]
}

/** Поставить щит на фишку. Второй на ту же фишку начинает срок заново. */
export function shieldToken(field: BonusField, token: TokenRef): BonusField {
  const rest = field.shields.filter(
    (shield) => !(shield.color === token.color && shield.token === token.index),
  )

  return {
    ...field,
    shields: [...rest, { color: token.color, token: token.index, left: SHIELD_TURNS }],
  }
}

/** Фишки под щитом — для withShields. */
export function shieldedRefs(field: BonusField): TokenRef[] {
  return field.shields.map((shield) => ({ color: shield.color, index: shield.token }))
}

/** Что оказалось в сундуке. Нет монет — внутри всегда что-то для партии. */
export function drawPrize(coins: boolean, random: () => number = Math.random): ChestPrize {
  const pool = (Object.keys(PRIZE_WEIGHT) as ChestPrize[]).filter(
    (prize) => coins || prize !== 'coins',
  )

  return weighted(pool, PRIZE_WEIGHT, random)
}

/** Новая подсыпка: 1–3 бонуса (на пустое поле — на один больше) на свободных клетках. */
function spawn(
  state: GameState,
  items: readonly BonusItem[],
  random: () => number,
): { cell: number; kind: BonusKind }[] {
  const lying = new Set(items.map((item) => item.index))
  const free = bonusCells().filter(
    (index) => ringOccupant(state, index) === null && !lying.has(index),
  )

  if (free.length === 0) return []

  const size = Number(
    weighted(Object.keys(DROP_WEIGHTS), DROP_WEIGHTS as Record<string, number>, random),
  )
  const count = Math.min(
    free.length,
    size + (items.length === 0 ? DROP_EMPTY_EXTRA : 0),
    MAX_ON_FIELD - items.length,
  )

  return shuffled(free, random)
    .slice(0, count)
    .map((cell) => ({ cell, kind: kind(random) }))
    .sort((a, b) => a.cell - b.cell)
}

function kind(random: () => number): BonusKind {
  return weighted(Object.keys(WEIGHT) as BonusKind[], WEIGHT, random)
}

function weighted<T extends string>(
  pool: T[],
  weights: Record<T, number>,
  random: () => number,
): T {
  const total = pool.reduce((sum, one) => sum + weights[one], 0)
  let roll = Math.floor(random() * total) + 1

  for (const one of pool) {
    roll -= weights[one]

    if (roll <= 0) return one
  }

  return pool[0]
}

function shuffled<T>(list: T[], random: () => number): T[] {
  const copy = [...list]

  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1))
    ;[copy[i], copy[j]] = [copy[j], copy[i]]
  }

  return copy
}
